export const CHECKOUT_URL =
  'https://senior-activity-ready.impultienda.ar/checkout'

export const OFFER_ANCHOR = '#offer'

export const PRODUCT_IMAGE =
  'https://cdn.magicpatterns.com/uploads/nvTmr8fGUh66Fi4j5krfRj/Diseno-sin-titulo-5.webp'

export const PRICE = {
  full: '$97 USD',
  now: '$27 USD',
}

/* ── WHAT IF YOUR NEXT SENIOR ACTIVITY WAS ALREADY READY? ── */
export const whatIfNoNeed = [
  'You don’t need another folder of random ideas.',
  'You don’t need to search online every time you need something new.',
  'You don’t need to reinvent your activity schedule every week.',
]

export const whatIfCategories = [
  'Cognitive activities.',
  'Social activities.',
  'Gentle movement.',
  'Creative projects.',
  'Group games.',
  'Activity planning.',
]

/* ── STOP PLANNING EVERY ACTIVITY FROM SCRATCH ── */
export const painQuestions = [
  'What can we do this afternoon?',
  'What will work with this group?',
  'What can I organize with the materials I already have?',
  'What can I use for a quieter session?',
  'What can I do with a larger group?',
  'How can I make this activity different from last week?',
  'What can I prepare without losing another hour?',
]

/* ── HAVING ACTIVITY IDEAS ISN’T THE SAME AS HAVING AN ACTIVITY SYSTEM ── */
export const scatteredFinds = [
  'Lists.',
  'Pins.',
  'Videos.',
  'Random games.',
  'Craft ideas.',
  'Conversation questions.',
]

export const systemGives = [
  'clear instructions,',
  'materials,',
  'preparation,',
  'facilitator prompts,',
  'group options,',
  'adaptations,',
  'activity variations,',
  'planning structures,',
  'and ready-to-use resources.',
]

/* ── TURN HOURS OF SEARCHING & PLANNING INTO A READY-TO-USE SYSTEM ── */
export const needIsThere = [
  { need: 'Need something cognitive?', answer: 'It’s there.' },
  { need: 'Need a social group activity?', answer: 'It’s there.' },
  { need: 'Need gentle recreational movement?', answer: 'It’s there.' },
  { need: 'Need a creative project?', answer: 'It’s there.' },
  { need: 'Need a group game?', answer: 'It’s there.' },
  {
    need: 'Need to organize your activity schedule?',
    answer: 'The framework is already there.',
  },
]

/* ── THE COMPLETE SYSTEM YOU’VE BEEN LOOKING FOR ── */
export const systemNots = [
  'Not another random activity list.',
  'Not another generic senior care guide.',
  'Not endless theory.',
]

export const systemChecks = [
  'Choose from multiple activity categories',
  'Access clear ready-to-use instructions',
  'Find activities for different group formats',
  'Adapt activities when needed',
  'Reduce constant searching and preparation',
  'Build more organized activity schedules',
]

/* ── WHAT’S INSIDE ── */
export interface Module {
  number: number
  title: string
  intro: string
  listIntro: string
  items: string[]
  after: string[]
  questions?: string[]
  tag: string
  color: string
}

export const modules: Module[] = [
  {
    number: 1,
    title: 'Cognitive & Brain Activities™',
    intro:
      'Bring structured mental engagement into your activity programming without constantly creating new material.',
    listIntro: 'Access practical activities involving:',
    items: [
      'memory',
      'attention',
      'language',
      'observation',
      'association',
      'conversation',
      'sequencing',
      'recognition',
      'problem solving',
      'word activities',
      'themed trivia',
      'category challenges',
      'storytelling',
      'guessing games',
    ],
    after: [
      'You’ll find actual prompts, questions, examples and facilitator instructions — not simply suggestions telling you what you should create.',
    ],
    tag: 'Open the activity. Follow the structure. Start engaging.',
    color: '#173654',
  },
  {
    number: 2,
    title: 'Gentle Movement Activities™',
    intro:
      'Add enjoyable movement without turning every activity into a formal fitness session.',
    listIntro: 'Access recreational activities using:',
    items: [
      'balloons',
      'lightweight balls',
      'scarves',
      'beanbags',
      'music',
      'targets',
      'cards',
      'simple objects',
      'seated participation',
      'rhythm',
      'coordination',
      'group interaction',
    ],
    after: [
      'Each activity provides practical instructions, materials, setup ideas and adaptations where appropriate.',
      'Different participants can engage in different ways according to comfort and ability.',
    ],
    tag: 'Simple movement. Enjoyable participation. Practical activities.',
    color: '#4f8580',
  },
  {
    number: 3,
    title: 'Social & Group Activities™',
    intro:
      'Stop struggling to create activities that actually get the group interacting.',
    listIntro: 'Access ready-to-use:',
    items: [
      'conversation games',
      'themed discussions',
      'partner activities',
      'group challenges',
      'collaborative storytelling',
      'preference games',
      'adult-appropriate icebreakers',
      'shared-experience prompts',
      'discussion activities',
      'social games',
    ],
    after: [
      'You’ll also have actual facilitator prompts covering themes such as food, music, hobbies, travel, seasons, entertainment, everyday life and personal preferences.',
      'Not just: “Start a conversation.” The conversation is already prepared for you.',
    ],
    tag: 'The conversation is already prepared for you.',
    color: '#c4933f',
  },
  {
    number: 4,
    title: 'Creative & Sensory Activities™',
    intro:
      'Bring more variety into your schedule with practical creative activities that don’t require participants — or staff — to be artists.',
    listIntro: 'Access:',
    items: [
      'simple art projects',
      'collaborative creations',
      'color activities',
      'music-inspired creativity',
      'tactile activities',
      'seasonal projects',
      'collage concepts',
      'decorating activities',
      'sensory exploration',
      'creative storytelling',
    ],
    after: [
      'Each activity includes the materials, preparation, instructions and practical variations needed to run it.',
      'Participants can create, select, discuss, organize, observe or contribute in different ways.',
    ],
    tag: 'Creative activities without complicated preparation.',
    color: '#b56f63',
  },
  {
    number: 5,
    title: 'Games & Recreation Activities™',
    intro: 'Go beyond repeating the same familiar games every week.',
    listIntro: 'Access a practical recreation library containing:',
    items: [
      'word games',
      'guessing games',
      'category games',
      'team activities',
      'observation challenges',
      'music games',
      'tabletop activities',
      'simple target games',
      'cooperative challenges',
      'seasonal recreation',
      'activities using everyday materials',
    ],
    after: [
      'Need something quickly? You’ll also find options designed for lower-preparation situations.',
    ],
    tag: 'Choose the game. Set it up. Run it.',
    color: '#6b7a4a',
  },
  {
    number: 6,
    title: 'Activity Planning System™',
    intro: 'This is where all the activities become an organized system.',
    listIntro:
      'Instead of randomly choosing what to do next, use practical planning frameworks based on:',
    items: [
      'activity category',
      'group size',
      'available time',
      'preparation time',
      'materials',
      'space',
      'participant preferences',
      'activity format',
    ],
    after: [
      'Access sample activity schedules, weekly planning structures, rotation ideas, decision frameworks and practical planning tools.',
    ],
    questions: [
      'Need something calm?',
      'Need something social?',
      'Need something creative?',
      'Need something for a larger group?',
      'Need something with almost no setup?',
    ],
    tag: 'Use the system to find the right direction faster.',
    color: '#6d6a97',
  },
]

/* ── 5 PREMIUM BONUSES ── */
export interface Bonus {
  number: number
  title: string
  intro: string[]
  listIntro: string
  items: string[]
  after: string[]
  tag: string
}

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'Senior Conversation & Connection Toolkit™',
    intro: [
      'Sometimes you don’t need another complicated activity.',
      'You need the right conversation.',
    ],
    listIntro:
      'Access ready-to-use conversation starters, themed discussions, storytelling prompts, preference activities, paired conversations, one-to-one formats and low-pressure social activities.',
    items: [
      'food',
      'travel',
      'hobbies',
      'music',
      'work',
      'seasons',
      'entertainment',
      'everyday life',
      'personal preferences',
    ],
    after: ['Actual questions and facilitator prompts are included.'],
    tag: 'Less improvising. More meaningful conversation.',
  },
  {
    number: 2,
    title: 'Music & Rhythm Activity Toolkit™',
    intro: [
      'Bring music into your activity programming without needing to be a musician.',
    ],
    listIntro: 'Access practical recreational activities using:',
    items: [
      'music discussions',
      'rhythm',
      'simple percussion',
      'listening',
      'recognition',
      'music categories',
      'seated participation',
      'group sound games',
      'music-based social activities',
    ],
    after: [
      'Clear instructions help you turn music into a structured activity instead of simply putting on a playlist.',
    ],
    tag: 'Press play on a better activity structure.',
  },
  {
    number: 3,
    title: 'Seasonal & Themed Activity Planner™',
    intro: ['A good theme can turn one activity into an entire experience.'],
    listIntro: 'Use practical frameworks built around:',
    items: [
      'seasons',
      'nature',
      'food',
      'travel',
      'music',
      'hobbies',
      'colors',
      'community',
      'celebrations',
      'general-interest themes',
    ],
    after: [
      'See how a single theme can connect conversation, recreation, creativity and group participation.',
      'Use the included planning structures to organize themed sessions without rebuilding everything yourself.',
    ],
    tag: 'Choose a theme. Build the experience.',
  },
  {
    number: 4,
    title: 'Senior Group Games Toolkit™',
    intro: ['Need something that gets the group participating?'],
    listIntro: 'Access ready-to-run recreational formats including:',
    items: [
      'word games',
      'category games',
      'guessing activities',
      'team challenges',
      'observation games',
      'cooperative games',
      'seated group games',
      'music games',
      'simple object-based activities',
    ],
    after: [
      'Clear rules. Practical setup. Facilitator examples. Adaptable formats.',
    ],
    tag: 'Less explaining. More playing.',
  },
  {
    number: 5,
    title: 'Senior Creative Projects Toolkit™',
    intro: [
      'Add practical creative options without turning your activity room into an art classroom.',
    ],
    listIntro: 'Access adult-appropriate:',
    items: [
      'creative projects',
      'collaborative activities',
      'collage ideas',
      'color-based activities',
      'decorating projects',
      'nature-inspired creativity',
      'visual storytelling',
      'simple seasonal creations',
    ],
    after: ['Materials, preparation and instructions are organized for you.'],
    tag: 'Creative engagement made easier to organize.',
  },
]

/* ── THIS ISN’T JUST ABOUT HAVING MORE ACTIVITY IDEAS ── */
export const outcomes = [
  {
    emoji: '🧠',
    title: 'Engage the Mind',
    description:
      'Use structured cognitive and conversation activities instead of searching for prompts.',
  },
  {
    emoji: '👥',
    title: 'Bring People Together',
    description:
      'Access social activities, group games and collaborative formats.',
  },
  {
    emoji: '🎨',
    title: 'Create More Variety',
    description:
      'Rotate creative, recreational, sensory and movement-based options.',
  },
  {
    emoji: '📋',
    title: 'Plan With Structure',
    description:
      'Use organized frameworks instead of staring at another blank schedule.',
  },
  {
    emoji: '⏱️',
    title: 'Spend Less Time Searching',
    description: 'Keep practical resources together in one system.',
  },
]

/* ── YOU DON’T NEED MORE IDEAS SCATTERED ACROSS THE INTERNET ── */
export const objections = [
  '“I already know plenty of activities.”',
  '“I’ve been doing this for years.”',
  '“I can find ideas online.”',
  '“I already have activities that work.”',
]

export const objectionSteps = [
  'Choose the category.',
  'Find the activity.',
  'Check the materials.',
  'Follow the instructions.',
  'Adapt when needed.',
  'Run the session.',
]

/* ── FROM “WHAT DO WE DO TODAY?” TO A COMPLETE ACTIVITY SYSTEM ── */
export const todayList = [
  'Ideas saved everywhere.',
  'Random websites bookmarked.',
  'Activities repeated because they’re easy.',
  'New sessions constantly being planned.',
  'Prompts created at the last minute.',
  'Materials gathered without a clear structure.',
]

export const tomorrowList = [
  'Your cognitive activities.',
  'Your social activities.',
  'Your movement options.',
  'Your creative resources.',
  'Your recreational games.',
  'Your planning system.',
  'Your specialized bonus toolkits.',
]

/* ── SPECIAL LAUNCH OFFER ── */
export const offerIncludes = [
  '6 Complete Activity Modules',
  '5 Premium Bonuses',
  'Cognitive Activities',
  'Social & Group Activities',
  'Gentle Movement Activities',
  'Creative & Sensory Activities',
  'Games & Recreation',
  'Activity Planning Tools',
]

/* ── YOUR PURCHASE IS 100% PROTECTED ── */
export const guaranteeSteps = [
  'Go through the six modules.',
  'Explore the activity resources.',
  'Check the planning system.',
  'Open the bonus toolkits.',
  'See how the material fits your professional activity programming.',
]

/* ── TESTIMONIALS ── */
export const testimonials = [
  {
    text: 'I run activities for 62 residents across two floors, and my Sunday nights used to disappear into Pinterest tabs. Now I open the planning system, look at the group size and the time I have, and the activity is already there with the materials listed. I got my weekends back and my afternoon sessions are noticeably fuller.',
    author: 'Margaret Whitfield',
    role: 'Activity Director, Assisted Living',
    location: 'Leeds, United Kingdom',
  },
  {
    text: 'The facilitator prompts are what sold me. I have been a recreation coordinator for eleven years and I still used to freeze when a conversation group went quiet. Having the actual questions written out — food, music, travel, seasons — completely changed how those sessions run. Our quieter residents are the ones talking most now.',
    author: 'Debra Ann Colwell',
    role: 'Recreation Coordinator, Senior Living Community',
    location: 'Columbus, Ohio, United States',
  },
  {
    text: 'I coordinate an adult day program with a very small budget and no dedicated activity staff. The gentle movement module using balloons, scarves and beanbags gave me two weeks of programming with things I already had in the cupboard. The adaptations meant nobody in the room had to sit out.',
    author: 'Susan Karalis',
    role: 'Adult Day Program Manager',
    location: 'Hamilton, Ontario, Canada',
  },
  {
    text: 'What I appreciate is that it does not try to teach me my job. I have thirty years with older adults. It simply organizes the activity side of the work, which was the part quietly eating my week. The seasonal planner alone let me build a whole themed month in one sitting.',
    author: 'Patricia Nguyen',
    role: 'Lifestyle Manager, Residential Aged Care',
    location: 'Adelaide, Australia',
  },
  {
    text: 'I am a caregiver looking after my mother and her two friends who visit weekly, and I was running out of ideas fast. The group games and creative projects gave me something new every visit without a shopping list or hours of preparation. Simple instructions, clear materials, and they genuinely enjoy it.',
    author: 'Yvonne Baptiste',
    role: 'Family Caregiver',
    location: 'Bristol, United Kingdom',
  },
]

/* ── FAQ ── */
export const faqs = [
  {
    q: 'Is this a physical product?',
    a: 'No. SENIOR ACTIVITY READY™ is a 100% digital professional activity resource.',
  },
  {
    q: 'Who is this designed for?',
    a: 'It is designed for Activity Directors, Senior Living Staff, Recreation Coordinators, Caregivers, Activity Professionals, Adult Day Program Staff and other professionals responsible for organizing activities for older adults.',
  },
  {
    q: 'Is this another generic list of senior activity ideas?',
    a: 'No. The focus is implementation. The system includes activity instructions, materials, facilitator prompts, practical variations, planning frameworks and ready-to-use resources.',
  },
  {
    q: 'Do I need to create all the activities myself?',
    a: 'No. The purpose of the system is to provide practical activity structures and resources you can use and adapt rather than beginning with a blank page.',
  },
  {
    q: 'Does it only include group games?',
    a: 'No. The system covers cognitive, social, creative, sensory, recreational and gentle movement activities, plus different participation formats.',
  },
  {
    q: 'Can activities be adapted?',
    a: 'Yes. Many resources include practical variations so professionals can adjust them according to their setting, available time, materials, group format and participant preferences.',
  },
  {
    q: 'Is this a fitness program?',
    a: 'No. Gentle movement appears as one recreational activity category, but SENIOR ACTIVITY READY™ is not a senior fitness or exercise training program.',
  },
  {
    q: 'Does this provide medical or therapeutic treatment?',
    a: 'No. SENIOR ACTIVITY READY™ is a recreational and educational activity resource. It does not provide medical advice, diagnosis, rehabilitation or individualized healthcare guidance.',
  },
  {
    q: 'Can this help reduce activity planning time?',
    a: 'That’s exactly what the system is designed to support: giving you organized resources so you don’t have to continuously search for and build every activity from zero.',
  },
  {
    q: 'How will I receive it?',
    a: 'You’ll receive digital access immediately after purchase.',
  },
]

/* ── ONE LAST QUESTION ── */
export const lastQuestionKeeps = [
  'You can keep searching online.',
  'Keep saving random ideas.',
  'Keep rebuilding activities.',
  'Keep creating new prompts.',
  'Keep repeating the same easy options.',
  'And keep facing the same question tomorrow.',
]

export const lastQuestionSteps = [
  'Choose the activity.',
  'Check the materials.',
  'Follow the structure.',
  'Adapt it to your group.',
  'And get started.',
]

/* ── SOCIAL PROOF POPUP ── */
export const purchases = [
  { name: 'Margaret W.', city: 'Manchester', country: 'United Kingdom' },
  { name: 'Linda R.', city: 'Toronto', country: 'Canada' },
  { name: 'Deborah K.', city: 'Phoenix, AZ', country: 'United States' },
  { name: 'Carol M.', city: 'Brisbane', country: 'Australia' },
  { name: 'Susan T.', city: 'Glasgow', country: 'United Kingdom' },
  { name: 'Patricia H.', city: 'Vancouver', country: 'Canada' },
  { name: 'Janet L.', city: 'Columbus, OH', country: 'United States' },
  { name: 'Helen B.', city: 'Melbourne', country: 'Australia' },
  { name: 'Barbara S.', city: 'Birmingham', country: 'United Kingdom' },
  { name: 'Diane C.', city: 'Calgary', country: 'Canada' },
  { name: 'Sandra P.', city: 'Tampa, FL', country: 'United States' },
  { name: 'Kathleen O.', city: 'Perth', country: 'Australia' },
]
